<template>
	<app-header-teleport>
		<app-back-button class="mr-2" />
		<div class="flex flex-1 items-center">
			<app-title :title="invoice.id ? `#${invoice.id}` : 'Create invoice'" class="flex" />
		</div>
	</app-header-teleport>
	<div class="flex flex-1 flex-col md:flex-row" v-if="!loadingDetails && itemsOptions.length > 0">
		<app-section class="flex flex-1 flex-col mt-4 md:mr-2">
			<app-title class="small" title="Invoice data" />
			<app-line />
			<div class="flex flex-1 gap-4 mt-4">
				<div class="flex w-1/2 flex-col gap-4">
					<restaurant-autocomplete class="w-full" v-model="invoice.restaurant" :object="true" />
					<app-input
						class="w-full"
						placeholder="Sales Person"
						name="salesPerson"
						v-model="salesPerson"
						:disabled="true"
					/>
				</div>
				<div class="flex w-1/2 flex-col gap-4">
					<app-datepicker
						placeholder="Invoice Date"
						:initial-value="invoice.docDate"
						class="w-full"
						@selected="(date) => onDateSelected('docDate', date)"
					/>
					<app-datepicker
						placeholder="Official Date"
						:initial-value="invoice.officialDate"
						class="w-full tight"
						@selected="(date) => onDateSelected('officialDate', date)"
					/>
				</div>
			</div>
			<div class="flex flex-1 gap-4 my-4">
				<div class="flex w-1/2 flex-col gap-4">
					<app-text-area
						placeholder="Notes"
						name="notes"
						v-model="invoice.description"
						class="w-full"
						style="height: 120px; resize: none;"
					/>
				</div>
				<div class="flex w-1/2 flex-col gap-4">
					<app-datepicker
						placeholder="Due Date"
						:initial-value="invoice.dueDate"
						@selected="(date) => onDateSelected('dueDate', date)"
					/>
					<div class="flex items-center justify-center w-full gap-4">
						<app-input
							class="w-full tight"
							placeholder="Official No."
							name="officialNo"
							v-model="invoice.officialNo"
						/>
						<div
							v-if="invoice.officialNo && invoice.fiscalizationUrl"
							@click="openUrl(invoice.fiscalizationUrl)"
							class="w-48 flex items-center justify-center cursor-pointer"
						>
							<span class="text-info leading-5 hover:underline ">View Off. Invoice</span>
						</div>
					</div>
				</div>
			</div>
		</app-section>
		<app-section class="flex flex-1 flex-col md:ml-2">
			<div class="flex items-center">
				<app-title class="flex flex-1 small" title="Invoice items" />
			</div>
			<app-line />
			<div class="flex flex-1 overflow-y-auto" style="max-height: 175px;">
				<app-table class="flex-1" :columns="columns" :data="invoice.items" :searchable="false">
					<template v-slot:type="{ item }">
						<span class="flex flex-1">
							{{ itemsOptions.find((i) => i.id === item.invoiceItem.id).description }}
						</span>
					</template>
					<template v-slot:amountNoVatCents="{ item }">
						<span class="flex flex-1 justify-end">
							{{ $filters.formatCurrency(item.amountNoVatCents / 100, invoice.restaurant.currency) }}
						</span>
					</template>
					<template v-slot:vatCents="{ item }">
						<span class="flex flex-1 justify-end">
							{{
								$filters.formatCurrency(
									(item.quantity * item.vatCents) / 100,
									invoice.restaurant.currency
								)
							}}
						</span>
					</template>
					<template v-slot:amountWithVat="{ item }">
						<span class="flex flex-1 justify-end">
							{{ $filters.formatCurrency(amountWithVAT(item) / 100, invoice.restaurant.currency) }}
						</span>
					</template>
					<template v-slot:actions="{ index }" v-if="!invoice.id">
						<app-confirmation
							placement="left"
							title="Are you sure you want to remove this item?"
							@confirm="removeItem(index)"
						>
							<app-button class="btn btn-warning transparent small" icon="cancel" />
						</app-confirmation>
					</template>
				</app-table>
			</div>
			<app-line />
			<div class="flex">
				<div class="flex flex-1 font-bold">Subtotal:</div>
				<div class="flex font-bold">
					{{ $filters.formatCurrency(invoiceAmountNoVatCents / 100, invoice.restaurant.currency) }}
				</div>
			</div>
			<div class="flex">
				<div class="flex flex-1">VAT:</div>
				<div class="flex">
					{{ $filters.formatCurrency(invoiceVatCents / 100, invoice.restaurant.currency) }}
				</div>
			</div>
			<app-line />
			<div class="flex">
				<div class="flex flex-1 font-bold">Total:</div>
				<div class="flex font-bold">
					{{
						$filters.formatCurrency(
							(invoiceAmountNoVatCents + invoiceVatCents) / 100,
							invoice.restaurant.currency
						)
					}}
				</div>
			</div>
			<div class="flex justify-between" v-if="invoice.id">
				<span>Paid: </span>
				<span :class="invoice['paidAmountCents'] / 100 > 0 ? 'text-success' : ''">
					{{ $filters.formatCurrency(invoice['paidAmountCents'] / 100, invoice.restaurant.currency) }}
				</span>
			</div>
			<div class="flex justify-between" v-if="invoice.id">
				<span>Unpaid: </span>
				<span :class="unpaidClass">
					{{ $filters.formatCurrency(invoice['unPaidAmountCents'] / 100, invoice.restaurant.currency) }}
				</span>
			</div>
		</app-section>
	</div>
	<app-section class="flex flex-1 flex-col" v-if="invoice.id">
		<div class="flex flex-row">
			<app-title title="Payments" class="flex flex-1 small" />
		</div>
		<app-line />
		<payment-table :payments="invoicePayments" />
	</app-section>
</template>

<script>
	import AppTitle from '@/components/app/AppTitle';
	import RestaurantAutocomplete from '@/components/sales/Autocomplete';
	import AppDatepicker from '@/components/app/AppDatepicker';
	import moment from 'moment';
	import FinanceService from '@/services/FinanceService';
	import AppTable from '@/components/app/AppTable';
	import AppConfirmation from '@/components/app/AppConfirmation';
	import PaymentTable from '@/components/finance/PaymentTable';
	import RouteNames from '@/router/names';

	export default {
		name: 'CreateInvoice',
		components: {
			PaymentTable,
			AppTitle,
			RestaurantAutocomplete,
			AppDatepicker,
			AppTable,
			AppConfirmation,
		},
		computed: {
			salesPerson() {
				try {
					return `${this.invoice.restaurant.accManager.name || ''} ${this.invoice.restaurant.accManager
						.surname || ''}`;
				} catch (e) {
					return '';
				}
			},
			invoiceAmountNoVatCents() {
				return this.invoice.items.reduce((tot, arr) => {
					return tot + parseInt(arr.amountNoVatCents) * parseInt(arr.quantity);
				}, 0);
			},
			invoiceVatCents() {
				return this.invoice.items.reduce((tot, arr) => {
					return tot + parseInt(arr.vatCents) * parseInt(arr.quantity);
				}, 0);
			},
			unpaidClass() {
				const unpaidAmount = this.invoice['unPaidAmountCents'] / 100;
				const totalAmount = (this.invoiceAmountNoVatCents + this.invoiceVatCents) / 100;
				if (unpaidAmount === totalAmount) {
					return 'bg-primary text-white px-1 rounded-md';
				} else if (unpaidAmount < totalAmount) {
					return 'bg-warning text-white px-1 rounded-md';
				}
				return '';
			},
		},
		data: () => ({
			RouteNames,
			loadingDetails: true,
			addingItem: false,
			creating: false,
			columns: [
				{ key: 'type', label: 'Type' },
				{ key: 'quantity', label: 'Quantity' },
				{ key: 'amountNoVatCents', label: 'Price' },
				{ key: 'vatCents', label: 'VAT' },
				{ key: 'amountWithVat', label: 'Amount with VAT' },
			],
			isCustomItemModalVisible: false,
			invoice: {
				docDate: moment().format('YYYY-MM-DD'),
				dueDate: moment().format('YYYY-MM-DD'),
				officialDate: moment().format('YYYY-MM-DD'),
				items: [],
				restaurant: { accManager: {} },
			},
			customItem: {
				invoiceItem: {},
			},
			itemsOptions: [],
			invoicePayments: [],
		}),
		methods: {
			getInvoiceDetails(id) {
				FinanceService.getInvoiceDetails(id)
					.then((details) => {
						this.invoice = {
							...details,
							restaurant: { ...details.restaurant, accManager: details.accManager },
						};
					})
					.finally(() => {
						this.loadingDetails = false;
						this.emitter.emit('isLoading', false);
					});
			},
			getInvoicePayments(id) {
				FinanceService.getInvoicePayments(id).then((invoicePayments) => {
					this.invoicePayments = invoicePayments;
				});
			},
			onDateSelected(key, value) {
				this.invoice[key] = value;
			},
			onItemDateSelected(key, value) {
				this.customItem[key] = value;
			},
			createInvoice() {
				this.creating = true;
				const amountNoVatCents = this.invoice.items.reduce((tot, arr) => {
					return tot + parseInt(arr.amountNoVatCents) * parseInt(arr.quantity);
				}, 0);
				const vatCents = this.invoice.items.reduce((tot, arr) => {
					return tot + parseInt(arr.vatCents) * parseInt(arr.quantity);
				}, 0);
				FinanceService.createInvoice({
					docDate: this.formatDate(this.invoice.docDate),
					dueDate: this.formatDate(this.invoice.dueDate),
					officialNo: this.invoice.officialNo,
					officialDate: this.formatDate(this.invoice.officialDate),
					amountNoVatCents: amountNoVatCents,
					vatCents: vatCents,
					amountWithVatCents: amountNoVatCents + vatCents,
					restaurant: {
						restaurantID: this.invoice.restaurant.restaurantID,
					},
					accManager: {
						id: this.invoice.restaurant.accManager.id,
					},
					items: this.invoice.items,
				})
					.then(() => {
						this.$notificationService.success('Invoice Created Successfully!');
						this.$router.back();
					})
					.catch((error) => {
						this.$notificationService.error(error);
					})
					.finally(() => {
						this.creating = false;
					});
			},
			getInvoiceItems() {
				FinanceService.getItems().then((itemsOptions) => {
					this.itemsOptions = itemsOptions;
				});
			},
			openUrl(url) {
				window.open(url, '_blank');
			},
			formatDate(date) {
				return moment(date).format('YYYY-MM-DD');
			},
			amountWithVAT(item) {
				return parseInt(item.quantity) * (parseInt(item.vatCents) + parseInt(item.amountNoVatCents));
			},
			async calculateItem(itemId) {
				if (
					this.invoice &&
					this.invoice.restaurant &&
					this.invoice.restaurant.restaurantID &&
					this.invoice.restaurant.restaurantID !== -1
				) {
					if (this.customItem.startDate !== '' && this.customItem.endDate !== '') {
						const calculatedValues = await FinanceService.calcItem(this.invoice.restaurant.restaurantID, {
							invoiceItem: {
								id: itemId,
							},
							startDate: this.formatDate(this.customItem.startDate),
							endDate: this.formatDate(this.customItem.endDate),
						});
						if (calculatedValues) {
							this.customItem.quantity = calculatedValues['quantity'];
							this.customItem.amountNoVatCents = calculatedValues['amountNoVatCents'];
							this.customItem.vatCents = calculatedValues['vatCents'];
						}
					}
				} else {
					this.$notificationService.warning('Please select a restaurant first!');
				}
			},
			removeItem(index) {
				this.invoice.items.splice(index, 1);
			},
		},
		mounted() {
			if (this.$route.params.id !== 'new') {
				this.invoice.id = this.$route.params.id;
				this.emitter.emit('isLoading', true);
				this.getInvoiceDetails(this.$route.params.id);
				this.getInvoicePayments(this.$route.params.id);
			} else {
				this.loadingDetails = false;
			}
			this.getInvoiceItems();
		},
		watch: {
			'customItem.invoiceItem'() {
				if (
					this.customItem &&
					this.customItem.invoiceItem &&
					(this.customItem.invoiceItem.id === 2 || this.customItem.invoiceItem.id === 1)
				) {
					this.calculateItem(this.customItem.invoiceItem.id);
				}
			},
			'customItem.endDate'() {
				if (this.customItem && this.customItem.endDate) {
					this.calculateItem(this.customItem.invoiceItem.id);
				}
			},
			'customItem.startDate'() {
				if (this.customItem && this.customItem.startDate) {
					this.calculateItem(this.customItem.invoiceItem.id);
				}
			},
		},
	};
</script>
